<script lang="ts">
  import {
    ArrowDownRight,
    ArrowUpRight,
    Calendar,
    ChevronDown,
    CircleDollarSign,
    Hash,
    Percent,
    Radio,
    Scale,
    Target,
    TrendingDown,
    TrendingUp,
  } from 'lucide-svelte';
  import { type PortfolioStats, type SessionSummary } from '$lib/api/sessions';
  import EquityCurveChart from '$lib/components/charts/EquityCurveChart.svelte';
  import { Card } from '$lib/components/ui/card';
  import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
  } from '$lib/components/ui/table';

  interface Props {
    sessions: SessionSummary[];
    stats: PortfolioStats | null;
    statsLoading: boolean;
  }

  let { sessions, stats, statsLoading }: Props = $props();

  // Colapsable: estado persistido en localStorage. Default: colapsado
  // (header inline con KPIs principales, sin ocupar mas de la mitad de
  // la pantalla cuando entras al tab).
  const STORAGE_KEY = 'rhdash:portfolio-expanded';
  function readInitialExpanded(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }
  let expanded = $state(readInitialExpanded());
  function toggle(): void {
    expanded = !expanded;
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(STORAGE_KEY, expanded ? '1' : '0');
      } catch {
        // ignore
      }
    }
  }

  let liveCount = $derived(sessions.filter((s) => s.isLive).length);

  // Symbol breakdown client-side (group por symbol del array filtrado).
  interface SymbolRow {
    symbol: string;
    sessions: number;
    totalPnL: number;
    trades: number;
    wins: number;
    winRate: number;
    winAmount: number;
    lossAmount: number;
    profitFactor: number | null;
  }

  let symbolRows = $derived.by((): SymbolRow[] => {
    const map = new Map<string, SymbolRow>();
    for (const s of sessions) {
      const key = s.symbol || 'Unknown';
      let row = map.get(key);
      if (!row) {
        row = {
          symbol: key,
          sessions: 0,
          totalPnL: 0,
          trades: 0,
          wins: 0,
          winRate: 0,
          winAmount: 0,
          lossAmount: 0,
          profitFactor: null,
        };
        map.set(key, row);
      }
      row.sessions += 1;
      row.totalPnL += s.totalPnL ?? 0;
      row.trades += s.tradeCount ?? 0;
      row.wins += s.wins ?? 0;
      row.winAmount += s.winAmount ?? 0;
      row.lossAmount += s.lossAmount ?? 0;
    }
    const arr = Array.from(map.values());
    for (const r of arr) {
      r.winRate = r.trades > 0 ? (r.wins / r.trades) * 100 : 0;
      r.profitFactor = r.lossAmount > 0 ? r.winAmount / r.lossAmount : null;
    }
    arr.sort((a, b) => b.totalPnL - a.totalPnL);
    return arr;
  });

  function fmtMoney(n: number): string {
    if (n === 0) return '$0.00';
    const sign = n > 0 ? '+' : '-';
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  }
  function fmtPF(pf: number | null): string {
    if (pf === null) return '∞';
    if (!Number.isFinite(pf)) return '—';
    return pf.toFixed(2);
  }
  function fmtDateRange(first: string | null, last: string | null): string {
    if (!first || !last) return '—';
    const f = new Date(first);
    const l = new Date(last);
    if (Number.isNaN(f.getTime()) || Number.isNaN(l.getTime())) return '—';
    const fmt = (d: Date) =>
      d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    if (fmt(f) === fmt(l)) return fmt(f);
    return `${fmt(f)} → ${fmt(l)}`;
  }
</script>

<Card class="border-gold/35 bg-card-hero lifted-edge-gold shadow-luxury-gold">
  <!-- HEADER: siempre visible. Click toggle expand/collapse. -->
  <button
    type="button"
    onclick={toggle}
    class="hover:bg-gold/5 group flex w-full items-center gap-4 rounded-t-xl px-8 py-5 text-left transition-colors"
    aria-expanded={expanded}
    aria-controls="portfolio-overview-body"
  >
    <!-- Icon + title -->
    <div class="flex items-center gap-2.5">
      <TrendingUp class="text-gold size-5" />
      <h3 class="text-gold text-display text-2xl">
        Portfolio Overview
      </h3>
    </div>

    <!-- Inline KPIs: Total P&L, Win rate, Trades (siempre visibles) -->
    <div class="flex flex-1 flex-wrap items-baseline gap-x-6 gap-y-1 sm:gap-x-10">
      <!-- Total P&L -->
      <div class="flex items-baseline gap-2">
        <span class="text-eyebrow">
          Total P&L
        </span>
        <span
          class={[
            'text-xl font-semibold tabular-nums',
            (stats?.totalProfit ?? 0) > 0 && 'text-success',
            (stats?.totalProfit ?? 0) < 0 && 'text-destructive',
            statsLoading && 'opacity-40',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {stats ? fmtMoney(stats.totalProfit) : '—'}
        </span>
      </div>

      <!-- Win rate -->
      <div class="flex items-baseline gap-2">
        <span class="text-eyebrow">
          Win rate
        </span>
        <span
          class={[
            'text-xl font-semibold tabular-nums',
            statsLoading && 'opacity-40',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {stats && stats.totalTrades > 0 ? `${stats.winRate.toFixed(1)}%` : '—'}
        </span>
      </div>

      <!-- Trades -->
      <div class="flex items-baseline gap-2">
        <span class="text-eyebrow">
          Trades
        </span>
        <span
          class={[
            'text-xl font-semibold tabular-nums',
            statsLoading && 'opacity-40',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {stats?.totalTrades ?? 0}
        </span>
      </div>

      <!-- Date range (oculto en mobile pequeno) -->
      {#if stats && stats.firstTradeTime}
        <div class="text-muted-foreground hidden items-center gap-1.5 text-xs lg:flex">
          <Calendar class="size-3" />
          {fmtDateRange(stats.firstTradeTime, stats.lastTradeTime)}
        </div>
      {/if}
    </div>

    <!-- Chevron indicador expand/collapse -->
    <ChevronDown
      class={[
        'text-muted-foreground group-hover:text-foreground size-5 shrink-0 transition-transform duration-200',
        expanded && 'rotate-180',
      ]
        .filter(Boolean)
        .join(' ')}
    />
  </button>

  <!-- BODY colapsable: stats detallados + symbol breakdown -->
  {#if expanded}
    <div id="portfolio-overview-body" class="border-border/50 space-y-6 border-t px-6 py-4">
      <!-- KPI grid extendido -->
      <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div class="space-y-1">
          <div class="text-muted-foreground flex items-center gap-1.5 text-xs">
            <Radio class="size-3" /> Live now
          </div>
          <div class="text-xl font-semibold tabular-nums">
            {liveCount}<span class="text-muted-foreground text-base font-normal">
              / {sessions.length}
            </span>
          </div>
        </div>

        <div class="space-y-1">
          <div class="text-muted-foreground flex items-center gap-1.5 text-xs">
            <ArrowUpRight class="size-3" /> Avg win
          </div>
          <div
            class={[
              'text-success text-xl font-semibold tabular-nums',
              statsLoading && 'opacity-40',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {stats && stats.totalWins > 0 ? fmtMoney(stats.avgWin) : '—'}
          </div>
        </div>

        <div class="space-y-1">
          <div class="text-muted-foreground flex items-center gap-1.5 text-xs">
            <ArrowDownRight class="size-3" /> Avg loss
          </div>
          <div
            class={[
              'text-destructive text-xl font-semibold tabular-nums',
              statsLoading && 'opacity-40',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {stats && stats.totalLosses > 0 ? fmtMoney(stats.avgLoss) : '—'}
          </div>
        </div>

        <div class="space-y-1">
          <div class="text-muted-foreground flex items-center gap-1.5 text-xs">
            <Scale class="size-3" /> Profit factor
          </div>
          <div
            class={[
              'text-xl font-semibold tabular-nums',
              statsLoading && 'opacity-40',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {stats && stats.totalTrades > 0 ? fmtPF(stats.profitFactor) : '—'}
          </div>
        </div>
      </div>

      <!-- Segunda fila: Max DD + Best/Worst trade + Sessions -->
      <div class="grid grid-cols-2 gap-4 border-t border-border/50 pt-4 md:grid-cols-4">
        <div class="space-y-0.5">
          <div class="text-muted-foreground flex items-center gap-1.5 text-xs">
            <TrendingDown class="size-3" /> Max drawdown
          </div>
          <div
            class={[
              'text-base font-semibold tabular-nums',
              (stats?.maxDrawdown ?? 0) < 0 && 'text-destructive',
              statsLoading && 'opacity-40',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {stats && stats.totalTrades > 0 ? fmtMoney(stats.maxDrawdown) : '—'}
          </div>
        </div>

        <div class="space-y-0.5">
          <div class="text-muted-foreground flex items-center gap-1.5 text-xs">
            <ArrowUpRight class="text-success size-3" /> Best trade
          </div>
          <div
            class={[
              'text-base font-semibold tabular-nums',
              (stats?.maxProfit ?? 0) > 0 && 'text-success',
              statsLoading && 'opacity-40',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {stats && stats.totalTrades > 0 ? fmtMoney(stats.maxProfit) : '—'}
          </div>
        </div>

        <div class="space-y-0.5">
          <div class="text-muted-foreground flex items-center gap-1.5 text-xs">
            <ArrowDownRight class="text-destructive size-3" /> Worst trade
          </div>
          <div
            class={[
              'text-base font-semibold tabular-nums',
              (stats?.maxLoss ?? 0) < 0 && 'text-destructive',
              statsLoading && 'opacity-40',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {stats && stats.totalTrades > 0 ? fmtMoney(stats.maxLoss) : '—'}
          </div>
        </div>

        <div class="space-y-0.5">
          <div class="text-muted-foreground flex items-center gap-1.5 text-xs">
            <Hash class="size-3" /> Sessions
          </div>
          <div class="text-base font-semibold tabular-nums">
            {sessions.length}
          </div>
        </div>
      </div>

      <!-- Equity curve (Lightweight Charts, eje X temporal real) -->
      {#if stats && stats.equityCurve.length > 1}
        <div class="space-y-2 border-t border-border/50 pt-4">
          <h3 class="text-muted-foreground text-xs font-medium uppercase tracking-wider">
            Equity curve
          </h3>
          <div class="rounded-md border bg-card/40 p-3">
            <EquityCurveChart points={stats.equityCurve} height={260} />
          </div>
        </div>
      {/if}

      <!-- Symbol breakdown -->
      {#if symbolRows.length > 0}
        <div class="space-y-2 border-t border-border/50 pt-4">
          <h3 class="text-muted-foreground text-xs font-medium uppercase tracking-wider">
            By symbol
          </h3>
          <div class="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Symbol</TableHead>
                  <TableHead class="text-right">Sessions</TableHead>
                  <TableHead class="text-right">Trades</TableHead>
                  <TableHead class="text-right">P&L</TableHead>
                  <TableHead class="text-right">Win %</TableHead>
                  <TableHead class="text-right">PF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {#each symbolRows as r (r.symbol)}
                  <TableRow>
                    <TableCell class="font-mono text-xs">{r.symbol}</TableCell>
                    <TableCell class="text-right tabular-nums text-xs">{r.sessions}</TableCell>
                    <TableCell class="text-right tabular-nums text-xs">{r.trades}</TableCell>
                    <TableCell
                      class={[
                        'text-right tabular-nums',
                        r.totalPnL > 0 && 'text-success',
                        r.totalPnL < 0 && 'text-destructive',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {fmtMoney(r.totalPnL)}
                    </TableCell>
                    <TableCell class="text-right tabular-nums text-xs">
                      {r.trades > 0 ? `${r.winRate.toFixed(0)}%` : '—'}
                    </TableCell>
                    <TableCell class="text-right tabular-nums text-xs">
                      {r.trades > 0 ? fmtPF(r.profitFactor) : '—'}
                    </TableCell>
                  </TableRow>
                {/each}
              </TableBody>
            </Table>
          </div>
        </div>
      {/if}
    </div>
  {/if}
</Card>
